import * as documentsRepo from "./repository";
import { COMPLETE_CONTENT_SEARCH } from "./repository/contentSearchStatus";
import type {
  ContentMatchInfo,
  ContentSearchStatus,
  DocumentRowMapped,
  ListParams,
} from "./repository";

/** Ids per hydration request — PostgREST encodes `.in()` in the request URL. */
const HYDRATE_CHUNK_SIZE = 100;

export interface FilteredContentSearchResult {
  /** Content matches that also pass every active filter, newest first. */
  documents: DocumentRowMapped[];
  /** Match locations for exactly the documents above. */
  matchInfo: Map<number, ContentMatchInfo>;
  status: ContentSearchStatus;
}

/**
 * Text search inside documents, held to the same filters as the filename list.
 *
 * The RPC only knows the building, project and folder, so its hits are
 * re-read through `listDocuments` with the view's filters plus an id
 * restriction. Hydrating them by id alone put rows a tag, folder, Verified or
 * added-date filter had excluded back into the list.
 */
export async function searchFilteredDocumentContent(
  filters: ListParams
): Promise<FilteredContentSearchResult> {
  const searchText = filters.searchText?.trim();
  if (!searchText) {
    return {
      documents: [],
      matchInfo: new Map(),
      status: COMPLETE_CONTENT_SEARCH,
    };
  }

  const { matches, status } = await documentsRepo.searchDocumentContentWithStatus(
    filters.buildingId,
    searchText,
    filters.projectIds?.length === 1 ? { projectId: filters.projectIds[0] } : undefined
  );

  const ids = Array.from(matches.keys());
  const documents: DocumentRowMapped[] = [];
  for (let i = 0; i < ids.length; i += HYDRATE_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + HYDRATE_CHUNK_SIZE);
    const { data } = await documentsRepo.listDocuments({
      ...filters,
      // The filename filter is what these hits did NOT match.
      searchText: undefined,
      documentIds: chunk,
      limit: chunk.length,
      offset: 0,
      sortKey: "created_at",
      sortOrder: "desc",
    });
    documents.push(...data);
  }
  documents.sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id - a.id);

  const matchInfo = new Map<number, ContentMatchInfo>();
  for (const document of documents) {
    const info = matches.get(document.id);
    if (info) matchInfo.set(document.id, info);
  }
  return { documents, matchInfo, status };
}
